import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import css from "./DishUpdateModal.module.css";
import {
  dishProductsAction,
  dishSemiFinishedActions,
} from "../../../../store/slices/dishes-slices";
import { dishesService } from "../../../../services/dishes-services";
import { DishAddProductModal } from "../DishAddProductModal/DishAddProductModal";
import { DishAddSemiFinishModal } from "../DishAddSemiFinishModal/DishAddSemiFinishModal";
import { Link } from "react-router-dom";
import { Fullscreen } from "lucide-react";
import UpdateDishComponent from "../../UpdateDishComponent/UpdateDishComponent";

const DishUpdateModal = ({ dish, setIsOpenUpdate }) => {
  const [dishProductAddOpen, setDishProductAddOpen] = useState(false);
  const [dishSemiFinishedAddOpen, setDishSemiFinishedAddOpen] = useState(false);
  const [completeDishData, setCompleteDishData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track if this is the initial load to prevent overwriting DB values
  const isInitialLoad = useRef(true);

  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const currentLanguage = i18n.language;

  const {
    dishProductsWeight,
    dishProductsWeightNetto,
    dishProductsCost,
    dishProductEnergy,
    dishProductsFats,
    dishProductsProteins,
    dishProductsCarbohydrates,
  } = useSelector((state) => state.dishProducts);

  const {
    dishSemiFinishedWeight,
    dishSemiFinishedWeightNetto,
    dishSemiFinishedCost,
    dishSemiFinishedEnergy,
    dishSemiFinishedFats,
    dishSemiFinishedProteins,
    dishSemiFinishedCarbohydrates,
  } = useSelector((state) => state.dishSemiFinished);

  const methods = useForm({ defaultValues: {}, mode: "onChange" });
  const { reset, setValue } = methods;

  // Fetch complete dish data on component mount
  useEffect(() => {
    const fetchCompleteDishData = async () => {
      try {
        setIsLoading(true);
        const response = await dishesService.getDishesById(dish.id);
        console.log("DishUpdateModal - Fetched complete dish data:", response);
        setCompleteDishData(response);
      } catch (error) {
        console.error(
          "DishUpdateModal - Error fetching complete dish data:",
          error
        );
        setCompleteDishData(dish);
      } finally {
        setIsLoading(false);
      }
    };

    if (dish?.id) {
      fetchCompleteDishData();
    } else {
      setIsLoading(false);
    }
  }, [dish?.id]);

  // Use complete dish data if available, otherwise fallback to provided dish data
  const dishData = completeDishData || dish;
  const {
    id,
    name,
    fats,
    products,
    weight,
    weightNetto,
    carbs,
    proteins,
    price,
    cost,
    categoryId,
    description,
    recipe,
    energy,
    semiFinisheds,
    categories,
  } = dishData;

  // Initialize products and semi-finished items
  useEffect(() => {
    if (!isLoading && completeDishData && products && semiFinisheds) {
      dispatch(dishProductsAction.updateArray(products));
      dispatch(dishSemiFinishedActions.updateArray(semiFinisheds));
      dispatch(dishProductsAction.changeTrigger());

      // Mark initial load as complete after a short delay to allow redux updates
      setTimeout(() => {
        isInitialLoad.current = false;
      }, 100);
    }
  }, [dispatch, products, semiFinisheds, isLoading, completeDishData]);

  // One-time reset for fields that don't change
  useEffect(() => {
    if (!isLoading && completeDishData) {
      reset({
        nameEn: name.en,
        nameRu: name.ru,
        nameKa: name.ge,
        nameAr: name.ar,
        descriptionEn: description.en,
        descriptionRu: description.ru,
        descriptionKa: description.ge,
        descriptionAr: description.ar,
        recipeEn: recipe.en,
        recipeRu: recipe.ru,
        recipeGe: recipe.ge,
        recipeAr: recipe.ar,
        price,
        categoryId: categoryId || "",
        weight: weight || 0,
        weightNet: weightNetto || 0,
        Cost: cost || 0,
        Energy: energy || 0,
        Fats: fats || 0,
        Proteins: proteins || 0,
        Carbohydrates: carbs || 0,
      });
    }
  }, [
    reset,
    name,
    description,
    recipe,
    price,
    categoryId,
    weight,
    weightNetto,
    cost,
    energy,
    fats,
    proteins,
    carbs,
    isLoading,
    completeDishData,
    categories,
    currentLanguage,
  ]);

  // Only recalculate values when ingredients change, not on initial load
  useEffect(() => {
    if (!isInitialLoad.current) {
      const totalWeight = dishProductsWeight + dishSemiFinishedWeight;
      const totalWeightNetto =
        dishProductsWeightNetto + dishSemiFinishedWeightNetto;
      const totalCost = dishProductsCost + dishSemiFinishedCost;
      const totalEnergy = dishProductEnergy + dishSemiFinishedEnergy;
      const totalFats = dishProductsFats + dishSemiFinishedFats;
      const totalProteins = dishProductsProteins + dishSemiFinishedProteins;
      const totalCarbs =
        dishProductsCarbohydrates + dishSemiFinishedCarbohydrates;

      setValue("weight", totalWeight);
      setValue("weightNet", totalWeightNetto);
      setValue("Cost", totalCost);
      setValue("Energy", totalEnergy);
      setValue("Fats", totalFats);
      setValue("Proteins", totalProteins);
      setValue("Carbohydrates", totalCarbs);
    }
  }, [
    dishProductsWeight,
    dishSemiFinishedWeight,
    dishProductsWeightNetto,
    dishSemiFinishedWeightNetto,
    dishProductsCost,
    dishSemiFinishedCost,
    dishProductEnergy,
    dishSemiFinishedEnergy,
    dishProductsFats,
    dishSemiFinishedFats,
    dishProductsProteins,
    dishSemiFinishedProteins,
    dishProductsCarbohydrates,
    dishSemiFinishedCarbohydrates,
    setValue,
  ]);

  return (
    <section className={css.overlay}>
      <div className={css.modal}>
        <div className={css.header}>
          <div className={css.title}>Update: {name?.[currentLanguage] || name?.en || ""}</div>
          <div className={css.headerButtons}>
            <div className={css.fullscreenButton}>
              <Link to={`/dishes/update-dish/${id}`} asChild>
                <Fullscreen size={16} />
              </Link>
            </div>
            <div
              className={css.closeButton}
              onClick={() => setIsOpenUpdate(false)}
            >
              ×
            </div>
          </div>
        </div>

        <UpdateDishComponent
          dishIdData={id}
          isModal={true}
          setIsOpenUpdate={setIsOpenUpdate}
        />

        {/* {isLoading ? (
          <div className={css.loadingContainer}>
            <div>{t("Loading...")}</div>
          </div>
        ) : (
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className={css.form}
            >
              <div className={css.infoBlock}>
                <div className={css.leftBlock}>
                  <DishLeftBlockCreateModal
                    setDishProductAddOpen={setDishProductAddOpen}
                    setDishSemiFinishedAddOpen={setDishSemiFinishedAddOpen}
                    setUploadedImage={setUploadedImage}
                    initialImage={img}
                    dishId={id}
                  />
                </div>

                <div className={css.rightBlock}>
                  <DishRightBlockCreateModal
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    handleCategoryChange={handleCategoryChange}
                  />
                </div>
              </div>

              <div className={css.buttonsBlock}>
                <button
                  type="button"
                  className={css.cancelButton}
                  onClick={() => setIsOpenUpdate(false)}
                >
                  {t("modal-cancel")}
                </button>
                <button type="submit" className={css.submitButton}>
                  OK
                </button>
              </div>
            </form>
          </FormProvider>
        )} */}
      </div>

      {!isLoading && dishProductAddOpen && (
        <DishAddProductModal setDishProductAddOpen={setDishProductAddOpen} />
      )}

      {!isLoading && dishSemiFinishedAddOpen && (
        <DishAddSemiFinishModal
          setDishSemiFinishedAddOpen={setDishSemiFinishedAddOpen}
        />
      )}
    </section>
  );
};

export { DishUpdateModal };
